// ============================================================================
// HOOK - useComponentLibrary.js
// ============================================================================
// ./hooks/useComponentLibrary.js

import { useContext, useCallback } from 'react';
import { GlobalContext } from '../context/provider';
import componentLibraryAPI from '../api/componentLibraryAPI';
// import { CourseDomains } from '../components/labEngine/splitCanvasArea';
import { CourseDomains } from '../types/courseDomains';

console.log('🔍 CourseDomains loaded:', CourseDomains);
console.log('🔍 CourseDomains keys:', Object.keys(CourseDomains));

const useComponentLibrary = () => {
  const { 
    componentLibraryState,
    componentLibraryDispatch
  } = useContext(GlobalContext);
  
  // ============================================================================
  // COMPONENT MANAGEMENT
  // ============================================================================

  // Get all components
  const getAllComponents = useCallback(async () => {
    componentLibraryDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await componentLibraryAPI.getAllComponents();

      if (response.success) {
        // Sort alphabetically
        const sortedComponents = response.data.sort((a, b) => 
          a.name.localeCompare(b.name)
        );
        componentLibraryDispatch({ type: 'SET_COMPONENTS', payload: sortedComponents });

        // Build grouped dynamically from all domains
        const allCategories = Object.values(CourseDomains)
          .flatMap(domain => domain.canvases)
          .flatMap(canvas => canvas.categories)
          .map(category => category.key);

        const grouped = Object.fromEntries(
          allCategories.map(key => [
            key,
            response.data.filter(c => c.category === key)
          ])
        );

        componentLibraryDispatch({ type: 'SET_ALL_COMPONENTS_BY_CATEGORY', payload: grouped });
      } else {
        componentLibraryDispatch({ type: 'SET_ERROR', payload: response.error || 'Failed to get components' });
      }
      return response;
    } catch (error) {
      componentLibraryDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [componentLibraryDispatch]);

  // Get components by category
  const getComponentsByCategory = useCallback(async (category) => {
    componentLibraryDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await componentLibraryAPI.getComponentsByCategory(category);
      if (response.success) {
        componentLibraryDispatch({ 
          type: 'SET_COMPONENTS_BY_CATEGORY', 
          payload: { category, components: response.data } 
        });
      } else {
        componentLibraryDispatch({ type: 'SET_ERROR', payload: response.error || 'Failed to get components' });
      }
      return response;
    } catch (error) {
      componentLibraryDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [componentLibraryDispatch]);

  const getComponentById = useCallback(async (componentId) => {
    componentLibraryDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await componentLibraryAPI.getComponentById(componentId);
      if (response.success) {
        componentLibraryDispatch({ type: 'SET_CURRENT_COMPONENT', payload: response.data });
      } else {
        componentLibraryDispatch({ type: 'SET_ERROR', payload: response.error || 'Failed to get component' });
      }
      return response;
    } catch (error) {
      componentLibraryDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [componentLibraryDispatch]);

  // ============================================================================
  // COMPONENT CRUD OPERATIONS
  // ============================================================================

  // Create component
  const createComponent = useCallback(async (componentData) => {
    componentLibraryDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await componentLibraryAPI.createComponent(componentData);
      if (response.success) {
        componentLibraryDispatch({ type: 'ADD_COMPONENT', payload: response.data });
      } else {
        componentLibraryDispatch({ type: 'SET_ERROR', payload: response.error || 'Failed to create component' });
      }
      return response;
    } catch (error) {
      componentLibraryDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [componentLibraryDispatch]);

  // Update component
  const updateComponent = useCallback(async (componentId, updateData) => {
    componentLibraryDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await componentLibraryAPI.updateComponent(componentId, updateData);
      if (response.success) {
        componentLibraryDispatch({ type: 'UPDATE_COMPONENT', payload: response.data });
      } else {
        componentLibraryDispatch({ type: 'SET_ERROR', payload: response.error || 'Failed to update component' });
      }
      return response;
    } catch (error) {
      componentLibraryDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [componentLibraryDispatch]);

  // Delete component
  const deleteComponent = useCallback(async (componentId) => {
    componentLibraryDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await componentLibraryAPI.deleteComponent(componentId);
      if (response.success) {
        componentLibraryDispatch({ type: 'DELETE_COMPONENT', payload: componentId });
      } else {
        componentLibraryDispatch({ type: 'SET_ERROR', payload: response.error || 'Failed to delete component' });
      }
      return response;
    } catch (error) {
      componentLibraryDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [componentLibraryDispatch]);

  // ============================================================================
  // SEARCH OPERATIONS
  // ============================================================================

  // Search components
  const searchComponents = useCallback(async (searchTerm) => {
    componentLibraryDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await componentLibraryAPI.searchComponents(searchTerm);
      if (response.success) {
        componentLibraryDispatch({ type: 'SET_SEARCH_RESULTS', payload: response.data });
      } else {
        componentLibraryDispatch({ type: 'SET_ERROR', payload: response.error || 'Failed to search components' });
      }
      return response;
    } catch (error) {
      componentLibraryDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [componentLibraryDispatch]);

  // ============================================================================
  // BULK IMPORT OPERATIONS
  // ============================================================================

  // Check duplicates before import
  const checkDuplicates = useCallback(async (category) => {
    componentLibraryDispatch({ type: 'SET_CHECKING_DUPLICATES', payload: true });
    try {
      const response = await componentLibraryAPI.checkDuplicates(category);
      if (response.success) {
        componentLibraryDispatch({ type: 'SET_DUPLICATES', payload: response.data.duplicates });
      } else {
        componentLibraryDispatch({ type: 'SET_ERROR', payload: response.error || 'Failed to check duplicates' });
        componentLibraryDispatch({ type: 'SET_CHECKING_DUPLICATES', payload: false });
      }
      return response;
    } catch (error) {
      componentLibraryDispatch({ type: 'SET_ERROR', payload: error.message });
      componentLibraryDispatch({ type: 'SET_CHECKING_DUPLICATES', payload: false });
      throw error;
    }
  }, [componentLibraryDispatch]);

  // Bulk import components
  const bulkImportComponents = useCallback(async (category, shouldUpdate = false, createdBy) => {
    componentLibraryDispatch({ type: 'SET_IMPORTING', payload: true });
    try {
      const response = await componentLibraryAPI.bulkImportComponents(category, shouldUpdate, createdBy);
      if (response.success) {
        componentLibraryDispatch({ type: 'SET_IMPORT_RESULT', payload: response.data });
        
        // Refresh components list after import
        await getAllComponents();
      } else {
        componentLibraryDispatch({ type: 'SET_ERROR', payload: response.message || 'Failed to import components' });
        componentLibraryDispatch({ type: 'SET_IMPORTING', payload: false });
      }
      return response;
    } catch (error) {
      componentLibraryDispatch({ type: 'SET_ERROR', payload: error.message });
      componentLibraryDispatch({ type: 'SET_IMPORTING', payload: false });
      throw error;
    }
  }, [componentLibraryDispatch, getAllComponents]);

  // Clear bulk import data
  const clearImportData = useCallback(() => {
    componentLibraryDispatch({ type: 'CLEAR_IMPORT_DATA' });
  }, [componentLibraryDispatch]);

  // Clear search results
  const clearSearchResults = useCallback(() => {
    componentLibraryDispatch({ type: 'CLEAR_SEARCH_RESULTS' });
  }, [componentLibraryDispatch]);

  /**
   * Validate component configuration
   * Calls backend validateComponentConfiguration method from componentLibraryModel
   * @param {string} componentId - Component library ID (componentId field, e.g., 'BRD_ESP32_001')
   * @param {Object} instanceConfig - Component instance configuration
   */
  const validateComponentConfiguration = useCallback(async (componentId, instanceConfig) => {

    console.log('🔍 ============================================');
  console.log('🔍 FRONTEND: validateComponentConfiguration called');
  console.log('🔍 Component ID:', componentId);
  console.log('🔍 Instance Config:', instanceConfig);
  console.log('🔍 ============================================');

    componentLibraryDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await componentLibraryAPI.validateComponentConfiguration(componentId, instanceConfig);
      console.log('✅ API Response:', response);
      if (response.success) {
        componentLibraryDispatch({ 
          type: 'SET_VALIDATION_RESULT', 
          payload: response.data 
        });
      } else {
        componentLibraryDispatch({ 
          type: 'SET_ERROR', 
          payload: response.error || 'Failed to validate component configuration' 
        });
      }
      
      return response;
    } catch (error) {
      console.error('❌ Frontend validation error:', error);
      componentLibraryDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    } finally {
      componentLibraryDispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [componentLibraryDispatch]);

  // ============================================================================
  // UTILITY OPERATIONS
  // ============================================================================

  // Clear error
  const clearError = useCallback(() => {
    componentLibraryDispatch({ type: 'CLEAR_ERROR' });
  }, [componentLibraryDispatch]);

  // Clear current component
  const clearCurrentComponent = useCallback(() => {
    componentLibraryDispatch({ type: 'CLEAR_CURRENT_COMPONENT' });
  }, [componentLibraryDispatch]);

  // Clear all data
  const clearData = useCallback(() => {
    componentLibraryDispatch({ type: 'CLEAR_DATA' });
  }, [componentLibraryDispatch]);

  // ============================================================================
  // RETURN VALUES
  // ============================================================================

  return {
    // State
    components: componentLibraryState.components,
    currentComponent: componentLibraryState.currentComponent,
    componentsByCategory: componentLibraryState.componentsByCategory,
    componentsByType: componentLibraryState.componentsByType,
    searchResults: componentLibraryState.searchResults,
    bulkImport: componentLibraryState.bulkImport, // ✅ NEW
    isLoading: componentLibraryState.isLoading,
    error: componentLibraryState.error,

    // Component Management
    getAllComponents,
    getComponentsByCategory,
    getComponentById,

    // CRUD Operations
    createComponent,
    updateComponent,
    deleteComponent,

    // Search Operations
    searchComponents,
    clearSearchResults,

    // Bulk Import Operations - ✅ NEW
    checkDuplicates,
    bulkImportComponents,
    clearImportData,

    // Utility
    clearError,
    clearCurrentComponent,
    clearData,
    validateComponentConfiguration,
  };
};

export default useComponentLibrary;